import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import subscriptionAPI from '../api/subscriptionAPI';
import './SubscriptionModal.css';

const SubscriptionModal = ({ isOpen, onClose, pricing, helper, initialFormData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: Configure, 2: Review, 3: Payment
  
  const [subscriptionData, setSubscriptionData] = useState({
    helperId: helper?._id || '',
    services: [{
      serviceType: initialFormData?.service || '',
      serviceOptions: initialFormData?.serviceOptions || {}
    }],
    schedule: {
      daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      timeSlots: [{
        startTime: initialFormData?.startTime || '09:00',
        endTime: initialFormData?.endTime || '11:00',
        applicableServices: [initialFormData?.service || '']
      }]
    },
    subscriptionPeriod: {
      startDate: getTomorrowDate(),
      endDate: getDateAfter30Days(),
      durationInDays: 30
    },
    customerAddress: initialFormData?.customerAddress || '',
    specialRequests: initialFormData?.specialRequests || ''
  });

  const [currentPricing, setCurrentPricing] = useState(pricing);
  const [loadingPricing, setLoadingPricing] = useState(false);

  const services = [
    { value: 'sweeping-mopping', label: 'Sweeping & Mopping', icon: '🧹' },
    { value: 'cooking', label: 'Cooking', icon: '👨‍🍳' },
    { value: 'laundry', label: 'Laundry', icon: '👕' },
    { value: 'gardening', label: 'Gardening', icon: '🌱' },
    { value: 'pet-care', label: 'Pet Care', icon: '🐕' },
    { value: 'elderly-care', label: 'Elderly Care', icon: '👴' },
    { value: 'babysitting', label: 'Babysitting', icon: '👶' },
    { value: 'hair-dresser', label: 'Hair Dresser', icon: '✂️' },
    { value: 'other', label: 'Other', icon: '🔧' }
  ];

  const apartmentTypes = [
    { value: '1bhk', label: '1 BHK', multiplier: 1.0 },
    { value: '2bhk', label: '2 BHK', multiplier: 1.5 },
    { value: '3bhk', label: '3 BHK', multiplier: 2.0 },
    { value: '4bhk', label: '4 BHK', multiplier: 2.5 },
    { value: 'more-than-4bhk', label: 'More than 4 BHK', multiplier: 3.0 }
  ];

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  function getTomorrowDate() {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }

  function getDateAfter30Days() {
    const future = new Date();
    future.setDate(future.getDate() + 31);
    return future.toISOString().split('T')[0];
  }

  // Generate time slots in 30-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour <= 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayTime = `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
        slots.push({ value: timeString, label: displayTime });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Calculate pricing when subscription data changes
  useEffect(() => {
    const calculatePricing = async () => {
      if (subscriptionData.helperId && subscriptionData.services.length > 0 && 
          subscriptionData.schedule.daysOfWeek.length > 0 && 
          subscriptionData.schedule.timeSlots.length > 0) {
        
        setLoadingPricing(true);
        try {
          // Add duration calculation for time slots
          const updatedSchedule = {
            ...subscriptionData.schedule,
            timeSlots: subscriptionData.schedule.timeSlots.map(slot => {
              const duration = calculateDuration(slot.startTime, slot.endTime);
              return { ...slot, duration };
            })
          };
          
          const pricingData = {
            ...subscriptionData,
            schedule: updatedSchedule
          };
          
          console.log('Calculating pricing for:', pricingData); // Debug log
          
          const response = await subscriptionAPI.calculatePricing(pricingData);
          console.log('Pricing response:', response); // Debug log
          
          if (response.success) {
            setCurrentPricing(response.pricing);
            setError(''); // Clear any previous errors
          } else {
            console.error('Pricing calculation failed:', response.message);
            setError('Failed to calculate pricing');
          }
        } catch (error) {
          console.error('Error calculating subscription pricing:', error);
          setError('Failed to calculate pricing');
        } finally {
          setLoadingPricing(false);
        }
      }
    };

    calculatePricing();
  }, [subscriptionData]);

  // Helper function to calculate duration
  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    
    if (endMinutes <= startMinutes) {
      return 0;
    }
    
    return (endMinutes - startMinutes) / 60;
  };

  const handleServiceToggle = (serviceValue) => {
    setSubscriptionData(prev => {
      const existingIndex = prev.services.findIndex(s => s.serviceType === serviceValue);
      let newServices;
      
      if (existingIndex >= 0) {
        // Remove service
        newServices = prev.services.filter(s => s.serviceType !== serviceValue);
      } else {
        // Add service
        newServices = [...prev.services, {
          serviceType: serviceValue,
          serviceOptions: serviceValue === 'sweeping-mopping' ? { apartmentType: '1bhk' } : {}
        }];
      }

      // Update time slots to include/exclude this service
      const updatedTimeSlots = prev.schedule.timeSlots.map(slot => ({
        ...slot,
        applicableServices: newServices.map(s => s.serviceType)
      }));

      return {
        ...prev,
        services: newServices,
        schedule: {
          ...prev.schedule,
          timeSlots: updatedTimeSlots
        }
      };
    });
  };

  const handleDayToggle = (day) => {
    setSubscriptionData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        daysOfWeek: prev.schedule.daysOfWeek.includes(day)
          ? prev.schedule.daysOfWeek.filter(d => d !== day)
          : [...prev.schedule.daysOfWeek, day]
      }
    }));
  };

  const handleTimeSlotChange = (index, field, value) => {
    setSubscriptionData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        timeSlots: prev.schedule.timeSlots.map((slot, i) => 
          i === index ? { ...slot, [field]: value } : slot
        )
      }
    }));
  };

  const addTimeSlot = () => {
    setSubscriptionData(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        timeSlots: [...prev.schedule.timeSlots, {
          startTime: '09:00',
          endTime: '11:00',
          applicableServices: prev.services.map(s => s.serviceType)
        }]
      }
    }));
  };

  const removeTimeSlot = (index) => {
    if (subscriptionData.schedule.timeSlots.length > 1) {
      setSubscriptionData(prev => ({
        ...prev,
        schedule: {
          ...prev.schedule,
          timeSlots: prev.schedule.timeSlots.filter((_, i) => i !== index)
        }
      }));
    }
  };

  const handleServiceOptionChange = (serviceType, optionName, value) => {
    setSubscriptionData(prev => ({
      ...prev,
      services: prev.services.map(service => 
        service.serviceType === serviceType 
          ? {
              ...service,
              serviceOptions: {
                ...service.serviceOptions,
                [optionName]: value
              }
            }
          : service
      )
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setSubscriptionData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setSubscriptionData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    if (subscriptionData.services.length === 0) {
      setError('Please select at least one service');
      return false;
    }

    if (subscriptionData.schedule.daysOfWeek.length === 0) {
      setError('Please select at least one day of the week');
      return false;
    }

    if (subscriptionData.schedule.timeSlots.length === 0) {
      setError('Please add at least one time slot');
      return false;
    }

    if (!subscriptionData.customerAddress.trim()) {
      setError('Please provide your address');
      return false;
    }

    // Validate sweeping-mopping services have apartment type
    const sweepingServices = subscriptionData.services.filter(s => s.serviceType === 'sweeping-mopping');
    for (const service of sweepingServices) {
      if (!service.serviceOptions.apartmentType) {
        setError('Please select apartment type for sweeping & mopping service');
        return false;
      }
    }

    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateForm()) {
      setStep(2);
      setError('');
    }
  };

  const handleCreateSubscription = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    
    try {
      console.log('Creating subscription with data:', subscriptionData); // Debug log
      
      const response = await subscriptionAPI.createSubscription(subscriptionData);
      console.log('Create subscription response:', response); // Debug log
      
      if (response.success) {
        const subscription = response.subscription;
        
        // Create payment order
        console.log('Creating payment order for subscription:', subscription._id);
        const paymentResponse = await subscriptionAPI.createPaymentOrder(subscription._id);
        console.log('Payment order response:', paymentResponse);
        
        if (paymentResponse.success) {
          // Process payment with Razorpay directly (not using processRazorpayPayment for bookings)
          const options = {
            key: 'rzp_test_pAuwoJJKm02oFw', // Correct Razorpay test key
            amount: paymentResponse.amount, // Amount in paise
            currency: paymentResponse.currency || 'INR',
            name: 'Sahayak',
            description: `Monthly Subscription Payment`,
            order_id: paymentResponse.orderId,
            image: '/logo192.png',
            method: {
              upi: true,
              card: true,
              netbanking: true,
              wallet: true,
              emi: false,
              paylater: false
            },
            config: {
              display: {
                blocks: {
                  utib: { //AXIS Bank
                    name: 'Pay using Axis Bank',
                    instruments: [
                      {
                        method: 'card'
                      },
                      {
                        method: 'netbanking'
                      },
                    ],
                  },
                  other: {
                    name: 'Other Payment Methods',
                    instruments: [
                      {
                        method: 'card',
                      },
                      {
                        method: 'netbanking',
                      },
                      {
                        method: 'upi'
                      },
                      {
                        method: 'wallet'
                      }
                    ],
                  },
                },
                hide: [
                  {
                    method: 'emi'
                  }
                ],
                sequence: ['block.utib', 'block.other'],
                preferences: {
                  show_default_blocks: true,
                },
              },
            },
            prefill: {
              name: helper?.name || 'Customer',
              email: '', // Add if available
              contact: '' // Add if available
            },
            notes: {
              subscription_id: subscription._id,
              type: 'subscription'
            },
            theme: {
              color: '#4F46E5'
            }
          };

          // Create Razorpay instance and handle payment
          const rzp = new window.Razorpay({
            ...options,
            handler: async function (razorpayResponse) {
              try {
                // Verify payment
                const verifyResponse = await subscriptionAPI.verifyPayment(subscription._id, {
                  razorpayPaymentId: razorpayResponse.razorpay_payment_id,
                  razorpayOrderId: razorpayResponse.razorpay_order_id,
                  razorpaySignature: razorpayResponse.razorpay_signature
                });

                if (verifyResponse.success) {
                  alert('Subscription created and payment completed successfully!');
                  onClose();
                  navigate('/my-subscriptions');
                } else {
                  setError('Payment verification failed. Please contact support.');
                }
              } catch (error) {
                console.error('Payment verification error:', error);
                setError('Payment verification failed. Please try again.');
              }
            }
          });

          rzp.on('payment.failed', function (response) {
            console.error('Payment failed:', response.error);
            setError(response.error.description || 'Payment failed. Please try again.');
          });

          // Open Razorpay checkout
          rzp.open();
        } else {
          setError(paymentResponse.message || 'Failed to create payment order. Please try again.');
        }
      } else {
        setError(response.message || 'Failed to create subscription');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      setError(`Failed to create subscription: ${error.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (!isOpen) return null;

  return (
    <div className="subscription-modal-overlay" onClick={onClose}>
      <div className="subscription-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📅 Create Monthly Subscription</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {error && <div className="error-message">{error}</div>}

          {step === 1 && (
            <div className="subscription-form">
              <h3>Configure Your Subscription</h3>

              {/* Services Selection */}
              <div className="form-group">
                <label>Select Services *</label>
                <div className="services-grid">
                  {services.map(service => (
                    <label
                      key={service.value}
                      className={`service-option ${
                        subscriptionData.services.some(s => s.serviceType === service.value) ? 'selected' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={subscriptionData.services.some(s => s.serviceType === service.value)}
                        onChange={() => handleServiceToggle(service.value)}
                      />
                      <span className="service-icon">{service.icon}</span>
                      <span className="service-label">{service.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Service Options */}
              {subscriptionData.services.filter(s => s.serviceType === 'sweeping-mopping').length > 0 && (
                <div className="form-group">
                  <label>Apartment Type for Sweeping & Mopping *</label>
                  <div className="apartment-grid">
                    {apartmentTypes.map(apt => (
                      <label
                        key={apt.value}
                        className={`apartment-option ${
                          subscriptionData.services.find(s => s.serviceType === 'sweeping-mopping')
                            ?.serviceOptions.apartmentType === apt.value ? 'selected' : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="apartmentType"
                          value={apt.value}
                          checked={
                            subscriptionData.services.find(s => s.serviceType === 'sweeping-mopping')
                              ?.serviceOptions.apartmentType === apt.value
                          }
                          onChange={(e) => 
                            handleServiceOptionChange('sweeping-mopping', 'apartmentType', e.target.value)
                          }
                        />
                        <span className="apartment-label">{apt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Days Selection */}
              <div className="form-group">
                <label>Select Days *</label>
                <div className="days-grid">
                  {daysOfWeek.map(day => (
                    <label
                      key={day.value}
                      className={`day-option ${
                        subscriptionData.schedule.daysOfWeek.includes(day.value) ? 'selected' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={subscriptionData.schedule.daysOfWeek.includes(day.value)}
                        onChange={() => handleDayToggle(day.value)}
                      />
                      <span>{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              <div className="form-group">
                <label>Time Slots *</label>
                {subscriptionData.schedule.timeSlots.map((timeSlot, index) => (
                  <div key={index} className="time-slot-row">
                    <select
                      value={timeSlot.startTime}
                      onChange={(e) => handleTimeSlotChange(index, 'startTime', e.target.value)}
                    >
                      {timeSlots.map(slot => (
                        <option key={slot.value} value={slot.value}>
                          {slot.label}
                        </option>
                      ))}
                    </select>
                    <span>to</span>
                    <select
                      value={timeSlot.endTime}
                      onChange={(e) => handleTimeSlotChange(index, 'endTime', e.target.value)}
                    >
                      {timeSlots.map(slot => (
                        <option key={slot.value} value={slot.value}>
                          {slot.label}
                        </option>
                      ))}
                    </select>
                    {subscriptionData.schedule.timeSlots.length > 1 && (
                      <button 
                        type="button" 
                        className="remove-slot-btn"
                        onClick={() => removeTimeSlot(index)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  className="add-slot-btn"
                  onClick={addTimeSlot}
                >
                  + Add Time Slot
                </button>
              </div>

              {/* Address */}
              <div className="form-group">
                <label htmlFor="customerAddress">Your Address *</label>
                <textarea
                  id="customerAddress"
                  name="customerAddress"
                  value={subscriptionData.customerAddress}
                  onChange={handleInputChange}
                  placeholder="Please provide your complete address"
                  rows="3"
                  required
                />
              </div>

              {/* Special Requests */}
              <div className="form-group">
                <label htmlFor="specialRequests">Special Requests (Optional)</label>
                <textarea
                  id="specialRequests"
                  name="specialRequests"
                  value={subscriptionData.specialRequests}
                  onChange={handleInputChange}
                  placeholder="Any specific requirements or instructions..."
                  rows="3"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="subscription-review">
              <h3>Review & Confirm</h3>
              
              <div className="review-section">
                <h4>Helper: {helper?.name}</h4>
                <p>📞 {helper?.phone} | ⭐ {helper?.averageRating?.toFixed(1)}</p>
              </div>

              <div className="review-section">
                <h4>Services:</h4>
                {subscriptionData.services.map((service, index) => (
                  <div key={index} className="service-item">
                    {services.find(s => s.value === service.serviceType)?.label}
                    {service.serviceOptions.apartmentType && 
                      ` (${service.serviceOptions.apartmentType.toUpperCase()})`
                    }
                  </div>
                ))}
              </div>

              <div className="review-section">
                <h4>Schedule:</h4>
                <p><strong>Days:</strong> {subscriptionData.schedule.daysOfWeek.map(day => 
                  day.charAt(0).toUpperCase() + day.slice(1)
                ).join(', ')}</p>
                {subscriptionData.schedule.timeSlots.map((slot, index) => (
                  <p key={index}>
                    <strong>Slot {index + 1}:</strong> {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                  </p>
                ))}
              </div>

              {currentPricing && (
                <div className="pricing-section">
                  <h4>Monthly Pricing:</h4>
                  <div className="pricing-row">
                    <span>Total Hours per Month:</span>
                    <span>{currentPricing.totalHoursPerMonth} hours</span>
                  </div>
                  <div className="pricing-row">
                    <span>Base Amount:</span>
                    <span>₹{currentPricing.monthlyBaseAmount}</span>
                  </div>
                  <div className="pricing-row">
                    <span>Platform Fee:</span>
                    <span>₹{currentPricing.platformFee}</span>
                  </div>
                  <div className="pricing-row">
                    <span>Payment Processing Fee:</span>
                    <span>₹{currentPricing.paymentProcessingFee}</span>
                  </div>
                  <div className="pricing-row total">
                    <span><strong>Total Monthly Amount:</strong></span>
                    <span><strong>₹{currentPricing.totalMonthlyAmount}</strong></span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === 1 && (
            <button 
              className="btn btn-primary"
              onClick={handleNextStep}
              disabled={loadingPricing}
            >
              {loadingPricing ? 'Calculating...' : 'Next: Review'}
            </button>
          )}
          
          {step === 2 && (
            <>
              <button 
                className="btn btn-secondary"
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleCreateSubscription}
                disabled={loading}
              >
                {loading ? 'Processing...' : `Pay ₹${currentPricing?.totalMonthlyAmount || 0}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal; 